// AcceleratorContent.jsx
import React, { useState } from "react";
import { motion } from "framer-motion";
import "../styles/RegulatoryCompliance.css";
import { CheckCircle, ChevronDown } from "lucide-react";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0 },
};

const services = [
  {
    title: "Early-Stage Incubation & Startup Bootcamps",
    description:
      "We guide student innovators and first-time entrepreneurs through the crucial first steps of their journey. Our structured incubation programs and intensive bootcamps help you:",
      lists: [
        "Validate and refine your business concept.",
        "Conduct market research and build a strong Minimum Viable Product (MVP).",
        "Understand market dynamics and customer behavior.",
        "Accelerate your go-to-market execution.",
      ],
      paragraph: "This foundation ensures you move from idea to action with clarity and speed.",
  },
  {
    title: "Mentorship & Coaching",
    description:
      "No entrepreneur succeeds alone. We connect you with experienced mentors, coaches, and industry experts who provide personalized, hands-on guidance tailored to your specific needs. From product development and strategic planning to leadership and fundraising, our network helps you navigate the complexities of building a thriving business.",
  },
  {
    title: "Investor Readiness & Access to Capital",
    description:
      "A great idea needs funding to scale. We prepare your venture for the investment world by:",
    lists: [
        "Perfecting your pitch deck.",
        "Building a robust financial model.",
        "Navigating valuation and investor negotiations.",
      ],
      paragraph: "Through our network of angel investors, venture capitalists, and private equity firms, we connect you directly to funding opportunities across Africa and beyond.",
  },
  {
    title: "Innovation Labs & Pitch Events",
    description:
      "Our innovation labs provide a collaborative environment to prototype, test, and refine your solutions. We host pitch events and demo days that give you the platform to showcase your venture to investors, strategic partners, and the broader entrepreneurial community — opening doors to funding, partnerships, and visibility.",
  },
  {
    title: "Business Scale-Up & Market Penetration",
    description:
      "Once your business is viable and funded, our work doesn’t stop. We provide ongoing support to help you:",
    lists: [
        "Develop scalable strategies and expansion plans.",
        "Build strong, high-performing teams.",
        "Enter new regional markets or diversify your product lines.",
      ],
      paragraph: "Our experts provide the data, insights, and strategies needed to ensure your growth is both rapid and sustainable.",
  },
];

const ComplianceContent = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (index) => {
    setOpenItems(prev => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <motion.div
      className="compliance-content"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="accelerator-image">
        <div className="accelerator-overlay">
          <h2 className="overlay-head">Nexa Accelerate</h2>
          <h3 className="italic-text">
            <em>Empowering Africa’s Next Generation of Entrepreneurs</em>
          </h3>
        </div>
      </div>
      <div className="compliance-text">
        <p>
          At Nexa, we believe Africa’s future is being built by its innovators and risk-takers. Nexa Accelerate is our dedicated service line designed to fuel this entrepreneurial revolution. We go beyond traditional business support by creating a complete ecosystem that transforms ambitious ideas into sustainable, market-ready ventures.
        </p>
      </div>
      <h2 className="core-head">Our Core Offerings</h2>
      <motion.div
        className="services-list"
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
      >
        {services.map((service, index) => (
          <motion.div
            key={index}
            className="service-card"
            variants={itemVariants}
          >
            <div className="service-header" onClick={() => toggleItem(index)}>
              <div className="service-title-section">
                <div className="icon-wrap">
                  <CheckCircle size={24} className="content-check-icon" />
                </div>
                <h4>{service.title}</h4>
              </div>
              <ChevronDown size={24} className={`chevron ${openItems[index] ? 'open' : ''}`} />
            </div>
            {openItems[index] && (
              <motion.div
                className="service-content"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <p>{service.description}</p>
                {service.lists && (
                  <ul className="service-listing">
                    {service.lists.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                )}
                <p>{service.paragraph}</p>
              </motion.div>
            )}
          </motion.div>
        ))}
      </motion.div>
              <p><strong>Nexa Accelerate isn’t just a program — it’s a launchpad.</strong> We empower Africa’s next generation of entrepreneurs with the skills, mentorship, resources, and funding they need to thrive in today’s competitive market.</p>
    </motion.div>
  );
};

export default ComplianceContent;
